import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Keeps the host application's address bar in sync with the chat's route when
 * the chat runs inside a host iframe (e.g. BIG's full-page /ai view).
 *
 * - Outbound: on every route change we post `EMBEDDED_ROUTE_CHANGE` to the
 *   parent. The host (StandardAppIntegration) reflects it under its own base
 *   path, so e.g. the chat at /thread/<id> shows as /ai/thread/<id> in BIG and
 *   a reload restores that thread.
 * - Inbound: we honor `NAVIGATE_TO_ROUTE` from the host (sent on browser
 *   back/forward) by navigating the chat router to the requested path.
 *
 * No-op when not embedded in an iframe. Hosts that don't understand these
 * messages (e.g. the report sidebar bridge) simply ignore the outbound one.
 */
export function useEmbeddedRouteSync() {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPathRef = useRef('');

    const isInIframe =
        typeof window !== 'undefined' && window.parent !== window;

    const path = location.pathname + location.search;
    currentPathRef.current = path;

    // Outbound: mirror our current route to the host.
    useEffect(() => {
        if (!isInIframe) return;
        window.parent.postMessage({ type: 'EMBEDDED_ROUTE_CHANGE', path }, '*');
    }, [isInIframe, path]);

    // Inbound: let the host drive navigation (its browser back/forward).
    useEffect(() => {
        if (!isInIframe) return;
        const handleMessage = (event: MessageEvent) => {
            const data = event.data;
            if (
                data &&
                data.type === 'NAVIGATE_TO_ROUTE' &&
                typeof data.path === 'string' &&
                data.path !== currentPathRef.current
            ) {
                navigate(data.path);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [isInIframe, navigate]);
}
